import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { BaseDrawer, DrawerHeader } from "@ryde/layout";
import { useReportId } from "@ryde/redux";
import {
  reportGroupingPeriodAF,
  RIGHTBAR_REPORT_CHART_WIDTH,
  useIsMobile,
} from "@ryde/shared";

import {
  buildReportChartExportFileName,
  useReportChartData,
  useReportChartDrawer,
  useReportChartDrawerResize,
  useReportChartToolbar,
} from "./parts";
import {
  IReportChartBarContentRef,
  ReportChartBarContent,
} from "./report-chart.bar-content";
import {
  ReportChartDrawerOuter,
  ReportChartDrawerResizeHandle,
  ReportChartDrawerWrapper,
} from "./report-chart.drawer.styled";
import { ReportChartToolbar } from "./report-chart.toolbar";

export const ReportChartDrawer: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { reportId } = useReportId();
  const { closeReportChartDrawer } = useReportChartDrawer();
  const chartRef = useRef<IReportChartBarContentRef>(null);
  const {
    reportTitle,
    dimensionOptions,
    metricOptions,
    dimensionColumnKey,
    selectedDimensionColumn,
    isReportGroupingDimension,
    groupingDimensionColumns,
    metricColumnKey,
    onDimensionChange,
    setMetricColumnKey,
    chartContent,
  } = useReportChartData();
  const periodGrouping = useRecoilValue(reportGroupingPeriodAF(reportId));

  const {
    displayMode,
    maxItemsInput,
    appliedMaxItems,
    refreshSeed,
    displayGrid,
    hideLabels,
    onDisplayModeChange,
    onMaxItemsInputChange,
    onMaxItemsBlur,
    onDisplayGridChange,
    onHideLabelsChange,
    onRefresh,
  } = useReportChartToolbar();

  const { drawerWidthStyle, onResizeHandleMouseDown, onResizeHandleKeyDown } =
    useReportChartDrawerResize();

  const dimensionLabel = useMemo(
    () =>
      dimensionOptions.find(o => o.value === dimensionColumnKey)?.label ?? "",
    [dimensionColumnKey, dimensionOptions],
  );

  const metricLabel = useMemo(
    () => metricOptions.find(o => o.value === metricColumnKey)?.label ?? "",
    [metricColumnKey, metricOptions],
  );

  const chartTitle = useMemo(() => {
    if (dimensionLabel && metricLabel) {
      return `${dimensionLabel}/${metricLabel}`;
    }
    return dimensionLabel || metricLabel;
  }, [dimensionLabel, metricLabel]);

  const exportFileName = useMemo(
    () => buildReportChartExportFileName({ reportTitle }),
    [reportTitle],
  );

  const hasChartData = chartContent.entities.length > 0;

  const onExportImage = (): void => {
    chartRef.current?.exportToImage();
  };

  return (
    <BaseDrawer
      onCloseDrawer={closeReportChartDrawer}
      customWidth={isMobile ? RIGHTBAR_REPORT_CHART_WIDTH : drawerWidthStyle}
    >
      <ReportChartDrawerOuter>
        {!isMobile && (
          <ReportChartDrawerResizeHandle
            onMouseDown={onResizeHandleMouseDown}
            onKeyDown={onResizeHandleKeyDown}
            role="separator"
            aria-orientation="vertical"
            aria-label={t("reports.chart.RESIZE_DRAWER")}
            tabIndex={0}
          />
        )}
        <ReportChartDrawerWrapper>
          <div className="ReportChartDrawer-Header">
            <DrawerHeader
              title={reportTitle || t("general.REPORTS")}
              onCloseDrawer={closeReportChartDrawer}
            />
          </div>
          <div className="ReportChartDrawer-ToolbarWrap">
            <ReportChartToolbar
              dimensionOptions={dimensionOptions}
              metricOptions={metricOptions}
              dimensionColumnKey={dimensionColumnKey}
              metricColumnKey={metricColumnKey}
              displayMode={displayMode}
              maxItemsInput={maxItemsInput}
              displayGrid={displayGrid}
              hideLabels={hideLabels}
              canExportImage={hasChartData}
              onDimensionChange={onDimensionChange}
              onMetricChange={setMetricColumnKey}
              onDisplayModeChange={onDisplayModeChange}
              onMaxItemsInputChange={onMaxItemsInputChange}
              onMaxItemsBlur={onMaxItemsBlur}
              onDisplayGridChange={onDisplayGridChange}
              onHideLabelsChange={onHideLabelsChange}
              onRefresh={onRefresh}
              onExportImage={onExportImage}
            />
          </div>
          <div className="ReportChartDrawer-ChartSection">
            <div className="ReportChartDrawer-ChartBox">
              {chartTitle && (
                <div className="ReportChartDrawer-ChartTitle">{chartTitle}</div>
              )}
              <div className="ReportChartDrawer-ChartInner">
                <ReportChartBarContent
                  ref={chartRef}
                  content={chartContent}
                  dimensionLabel={dimensionLabel}
                  metricLabel={metricLabel}
                  dimensionColumn={selectedDimensionColumn}
                  isReportGroupingDimension={isReportGroupingDimension}
                  groupingDimensionColumns={groupingDimensionColumns}
                  periodGrouping={periodGrouping}
                  displayMode={displayMode}
                  maxDisplay={appliedMaxItems}
                  refreshSeed={refreshSeed}
                  displayGrid={displayGrid}
                  hideLabels={hideLabels}
                  exportFileName={exportFileName}
                />
              </div>
            </div>
          </div>
        </ReportChartDrawerWrapper>
      </ReportChartDrawerOuter>
    </BaseDrawer>
  );
};
